import { fileToBase64 } from '@lib/files/file-to-base64';
import { DataFrame } from '@lib/mobx/dataframe';
import { SearchResult } from '@stores/Library/LibrarySearch';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media';
import { action, computed, makeObservable, observable } from 'mobx';
import { throttle } from 'throttle-debounce';
import { LibraryManager } from './LibraryManager';

type SearchResultItem = {
  id: string;
  score: number;
  mediaId: string;
  libraryId: string;
  startTime: number;
  endTime: number;
};

type FetchReult = {
  results: {
    engine: string;
    domain: string;
    results: SearchResultItem[];
  }[];
  media: Media[];
};

type SearchRequest = {
  text?: string;
  image?: string;
  sample?: { libraryId: string; mediaId: string; timestamp: number };
};

export class LibraryManagerOptimizedSearch extends DataFrame<SearchResult, FetchReult> {
  @observable
  request: SearchRequest | null = null;
  @observable
  limit = 100;
  @observable
  skip = 0;

  @computed
  get hasSearchTerms() {
    return !!this.request;
  }

  constructor(readonly libraryManager: LibraryManager) {
    super();
    makeObservable(this);

    const throttledUpdate = throttle(1000, () => this.update(), {
      noLeading: true,
      noTrailing: false,
    });

    // react to the object being replaced
    this.reaction(
      // crude implementaiton to detect changes in the whole object
      // deepObserve doesn't work because we replace the object with a new one
      () => JSON.stringify(this.request),
      throttledUpdate,
    );
    this.reaction(() => this.libraryManager.selectedLibraryIdsToSearch.size, throttledUpdate);

    this.reaction(() => this.limit, throttledUpdate);
    this.reaction(() => this.skip, throttledUpdate);
  }

  @action
  setSearchText(text: string | null) {
    if (!this.request) this.request = {} as SearchRequest;

    if (text === null) {
      delete this.request.text;
      if (Object.keys(this.request).length === 0) {
        this.request = null;
      }
    } else {
      this.request.text = text;
    }
  }

  @action
  setSearchImage(image: File | null) {
    if (!this.request) this.request = {} as SearchRequest;

    const request = this.request;
    if (image === null) {
      delete request.image;
      if (Object.keys(this.request).length === 0) {
        this.request = null;
      }
    } else {
      fileToBase64(image)
        .then(
          action((b64Image: string) => {
            request.image = b64Image;
          }),
        )
        .catch((e: Error) => {
          console.error(e);
        });
    }
  }

  @action
  setSearchSample(sample: { libraryId: string; mediaId: string; timestamp: number } | null) {
    if (!this.request) this.request = {} as SearchRequest;

    if (sample === null) {
      delete this.request.sample;
      if (Object.keys(this.request).length === 0) {
        this.request = null;
      }
    } else {
      this.request.sample = sample;
    }
  }

  @action
  clearSearch() {
    this.request = null;
    this.skip = 0;
    this.limit = 100;
  }

  @action
  setSearchRequest(request: SearchRequest | null) {
    this.request = request;
  }

  async fetch(): Promise<FetchReult> {
    if (!this.hasSearchTerms || this.libraryManager.selectedLibrariesToSearch.length === 0) {
      return { results: [], media: [] };
    }

    return userStore.apiWithAuth(`/search/visual?limit=${this.limit}&offset=${this.skip}`, {
      method: 'POST',
      body: {
        criteria: this.request,
        libraryIds: Array.from(this.libraryManager.selectedLibraryIdsToSearch),
      },
    });
  }

  protected override postProcess(fetchedData: FetchReult): SearchResult {
    const mediaObject: Record<string, Media> = {};
    for (const media of fetchedData.media) {
      mediaObject[media._id] = media;
    }

    const allItems = fetchedData.results.flatMap((r) => r.results);

    return {
      results: allItems,
      media: mediaObject,
    };
  }
}
